/**
 * WANN EINE STANDORTMESSUNG GUT GENUG IST - und welche von zweien gilt.
 *
 * ORIGINALTEXT MARVIN (15.09.2026)
 * > „Bei den Bildern musst du die zwingend georeferenzierte Daten auf das Foto
 * >  bringen."
 *
 * ## DIE MESSUNG LÄUFT, WÄHREND DIE KAMERA OFFEN IST
 *
 * Das ist die ganze Idee hinter dieser Datei. Ein Telefon braucht nach dem
 * ersten `getCurrentPosition` zwischen 2 und 20 Sekunden, bis es von der
 * Funkzelle (2000 Meter) auf die Satelliten (5 Meter) umgestellt hat. Wer
 * erst NACH der Aufnahme fragt, steht mit dem Foto in der Hand da und wartet
 * - und weil das lästig ist, nimmt er beim dritten Mal den anderen Weg.
 *
 * **Die Kamera ist dabei kein Hindernis, sondern der Zeitgewinn:** zwischen
 * dem Tipp auf den Knopf und dem fertigen Foto vergehen ohnehin 5 bis 15
 * Sekunden, und genau in dieser Zeit läuft `watchPosition` mit. Was bis zur
 * Rückkehr hereingekommen ist, ist die Messung; die BESTE davon zählt und
 * nicht die letzte.
 *
 * ## DIE BESTE UND NICHT DIE LETZTE
 *
 * `watchPosition` meldet immer wieder, und die Werte werden nicht monoton
 * besser: nach 5 guten Meldungen kommt eine mit 800 Metern, weil das Gerät
 * kurz keine Sicht hatte. Wer einfach die letzte nimmt, brennt die 800 auf
 * das Foto. Verglichen wird deshalb die GENAUIGKEIT - und bei gleich guter
 * die neuere, denn die gehört näher an den Auslösezeitpunkt.
 *
 * ## KEIN REACT UND KEIN BROWSER IN DIESER DATEI
 *
 * Aus demselben Grund wie `wischen.ts`, `zoomen.ts` und die übrigen
 * Regeldateien: eine Datei mit JSX lässt sich mit `--experimental-strip-types`
 * nicht laden, und dann bliebe nur die Textsuche. **Eine Textsuche misst,
 * dass etwas dasteht, nicht, dass es stimmt.** Welche von 2 Messungen
 * gewinnt, muss man nachrechnen.
 */

/** Was eine Messung mitbringt - dieselben Namen wie `GeolocationCoordinates`. */
export interface Messung {
  lat: number;
  lon: number;
  /** Meter. Der Browser nennt es `accuracy`. */
  genauigkeit: number;
  hoehe: number | null;
  richtung: number | null;
  /** Millisekunden seit 1970, wie `GeolocationPosition.timestamp`. */
  zeit: number;
}

/**
 * AB HIER WIRD NICHT MEHR GEWARTET.
 *
 * 20 Meter sind auf einer Baustelle die Hausnummer: das Nachbarhaus steht
 * 12 bis 15 Meter weiter. Besser geht immer, aber wer darauf wartet, wartet
 * auf einen Wert, den das Gerät zwischen Häusern gar nicht liefern kann -
 * **eine Schwelle, die selten erreicht wird, ist dasselbe wie gar keine.**
 */
export const GUT_GENUG_METER = 20;

/**
 * WIE LANGE NACH DER RÜCKKEHR AUS DER KAMERA NOCH GEWARTET WIRD.
 *
 * Nur, wenn bis dahin NICHTS hereingekommen ist. Steht schon eine Messung
 * da, wird das Foto sofort gesendet - eine bessere Zahl ist keinen Stillstand
 * wert, den der Monteur vor der Grube als hängende Plattform erlebt.
 *
 * ## VON 4 AUF 15 SEKUNDEN, UND DAS IST DIE BEHEBUNG VOM 18.09.2026
 *
 * ORIGINALTEXT MARVIN (18.09.2026)
 *   „es gibt einen fehler beim aufnehmen von einem foto direkt aus der
 *    software, du drückst auf den knopf, du kannst das foto aufnehmen am
 *    handy, sagst „Foto benutzen" aber dann wird es verworfen, es passiert
 *    nichts, es wird nicht gespeichert."
 *
 * 4 Sekunden waren gerechnet für ein Gerät, das WÄHREND der Aufnahme
 * weitermisst. Genau das tut es nicht: solange die Kamera des Betriebssystems
 * offen ist, liegt die Seite im Hintergrund, und iOS wie Android halten dort
 * die Rückrufe von `watchPosition` an. Gemessen wird also erst ab der
 * Rückkehr - und ein kalter GPS-Fix braucht 5 bis 20 Sekunden. **Die Wartezeit
 * war kürzer als das, worauf sie wartete**, und das Foto fiel durch.
 *
 * Die Zahl allein reicht nicht: dazu gehört ein zweiter, einmaliger
 * `getCurrentPosition` nach der Rückkehr (die angehaltene Wache liefert nicht
 * immer von selbst wieder), und vor allem, dass das Foto NICHT mehr verworfen
 * wird, wenn auch das nichts bringt.
 */
export const NACHWARTEN_MS = 15000;

/**
 * WIE LANGE DER BROWSER SELBST HÖCHSTENS SUCHEN DARF.
 *
 * `timeout` in den Optionen von `watchPosition`. Ohne ihn wartet ein Gerät
 * ohne Empfang ewig, und der Knopf sieht aus wie kaputt.
 */
export const BROWSER_TIMEOUT_MS = 20000;

/**
 * DIE OPTIONEN, MIT DENEN GEFRAGT WIRD.
 *
 * `enableHighAccuracy` ist der ganze Unterschied: ohne ihn nimmt das Gerät
 * die Funkzelle und antwortet in 200 Millisekunden mit 2000 Metern. Mit ihm
 * schaltet es GPS ein. **`maximumAge: 0` gehört dazu** - sonst reicht es eine
 * Messung von vor 10 Minuten durch, und die stammt vom letzten Auftrag.
 */
export const OPTIONEN = {
  enableHighAccuracy: true,
  timeout: BROWSER_TIMEOUT_MS,
  maximumAge: 0,
} as const;

/**
 * Was `watchPosition` liefert, auf das Nötige gebracht.
 *
 * ## EINE RICHTUNG OHNE BEWEGUNG IST KEINE RICHTUNG
 *
 * Die Geolocation-Schnittstelle sagt es selbst: steht das Gerät still
 * (`speed` ist 0), ist `heading` bedeutungslos. Manche Browser liefern dann
 * `null`, andere `NaN`, und wieder andere schlicht **0 - also Norden**. Wer
 * ein Foto macht, steht still; ohne diese Zeile stünde auf fast jedem
 * Nachweisfoto dieses Hauses „Blick N (0 Grad)", und zwar falsch.
 * Gemessen am 16.09.2026 an einem Prüflauf, der genau das aufs Bild brannte.
 *
 * ## UND EINE HÖHE OHNE IHRE GENAUIGKEIT IST KEINE HÖHE
 *
 * Dieselbe Regel wie bei den Koordinaten, eine Etage tiefer. `altitude` ohne
 * `altitudeAccuracy` ist ein Wert, den niemand einordnen kann - und eine
 * Ellipsoidhöhe von 0 liegt in Deutschland rund 45 Meter UNTER dem
 * Meeresspiegel. Sie ist damit kein Messwert, sondern ein nicht gesetzter
 * Standardwert, der aussieht wie einer.
 */
export function alsMessung(p: {
  coords: { latitude: number; longitude: number; accuracy: number;
            altitude?: number | null; altitudeAccuracy?: number | null;
            heading?: number | null; speed?: number | null };
  timestamp: number;
}): Messung | null {
  const lat = Number(p?.coords?.latitude);
  const lon = Number(p?.coords?.longitude);
  const g = Number(p?.coords?.accuracy);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  /* EINE MESSUNG OHNE GENAUIGKEIT WIRD VERWORFEN UND NICHT GERETTET. Sie
     käme sonst mit einem erfundenen Ersatzwert auf ein Nachweisfoto, und
     genau das schliesst „zwingend" aus. Der Server weist sie ohnehin ab
     (`regeln/geostempel.ts`); hier fällt sie vorher heraus, damit statt
     dessen die vorige, gute Messung gilt. */
  if (!Number.isFinite(g) || g <= 0) return null;

  const h = Number(p.coords.altitude);
  const hg = Number(p.coords.altitudeAccuracy);
  const r = Number(p.coords.heading);
  const v = Number(p.coords.speed);
  return {
    lat, lon, genauigkeit: g,
    hoehe: Number.isFinite(h) && Number.isFinite(hg) && hg > 0 ? h : null,
    richtung: Number.isFinite(r) && Number.isFinite(v) && v > 0 ? r : null,
    zeit: Number(p.timestamp) || Date.now(),
  };
}

/**
 * WELCHE VON 2 MESSUNGEN GILT. Die genauere; bei gleicher die neuere.
 *
 * `null` auf einer Seite heisst: es gibt sie nicht, also gilt die andere.
 */
export function besser(a: Messung | null, b: Messung | null): Messung | null {
  if (!a) return b;
  if (!b) return a;
  if (b.genauigkeit < a.genauigkeit) return b;
  if (b.genauigkeit > a.genauigkeit) return a;
  return b.zeit >= a.zeit ? b : a;
}

/** Reicht das, um nicht weiter zu warten? */
export function gutGenug(m: Messung | null): boolean {
  return !!m && m.genauigkeit <= GUT_GENUG_METER;
}

/**
 * Die Felder, die mit dem Foto in das Formular gehen. Die Namen sind die,
 * die `routen/dateien.ts` liest - **eine Umbenennung hier ohne eine dort
 * ist ein Foto ohne Stempel, und das fällt niemandem auf.**
 */
export function alsFelder(m: Messung): Record<string, string> {
  const f: Record<string, string> = {
    lat: String(m.lat),
    lon: String(m.lon),
    genauigkeit: String(m.genauigkeit),
    aufgenommen_am: new Date(m.zeit).toISOString(),
  };
  if (m.hoehe !== null) f.hoehe = String(m.hoehe);
  if (m.richtung !== null) f.richtung = String(m.richtung);
  return f;
}

/**
 * WAS DER MONTEUR LIEST, WÄHREND GEMESSEN WIRD.
 *
 * **Eine Fläche, die nichts sagt, ist von einer kaputten nicht zu
 * unterscheiden** - dieselbe Regel wie am Abwurffeld. Die Zahl steht dabei,
 * sobald es eine gibt: wer 300 Meter sieht, geht 3 Schritte aus dem Hausflur
 * heraus, und das ist genau die Handlung, die hier helfen würde.
 */
export function standortsatz(m: Messung | null, sucht: boolean): string {
  if (m && gutGenug(m)) return `Standort: ± ${Math.round(m.genauigkeit)} m`;
  if (m) {
    return `Standort noch ungenau (± ${Math.round(m.genauigkeit)} m)`
         + (sucht ? ', es wird weiter gemessen' : '');
  }
  return sucht ? 'Der Standort wird gesucht' : 'Noch kein Standort';
}

/**
 * DER SATZ ZUR ABLEHNUNG DURCH DEN BROWSER.
 *
 * Er nennt den WEG und nicht nur den Zustand: „Ortung ist aus" sagt niemandem,
 * was zu tun ist. Die Codes stehen in der Geolocation-Schnittstelle;
 * `undefined` heisst, der Browser kennt die Schnittstelle gar nicht.
 */
export function ortungsfehler(code: number | undefined): string {
  if (code === 1) {
    return 'Dieser Browser darf den Standort nicht sehen. In den '
         + 'Einstellungen der Seite den Standortzugriff erlauben und den '
         + 'Knopf noch einmal tippen.';
  }
  if (code === 2) {
    return 'Das Gerät findet seinen Standort nicht. Im Freien noch einmal '
         + 'versuchen - zwischen Mauern und im Keller hat es keine Sicht zu '
         + 'den Satelliten.';
  }
  if (code === 3) {
    return 'Das Gerät hat den Standort nicht rechtzeitig gefunden. Im Freien '
         + 'noch einmal versuchen.';
  }
  return 'Dieses Gerät kann seinen Standort nicht melden.';
}
